"use client";

import { INVEST_NOW_MODAL_KEY } from "@repo/shared-utils/shared.constants";
import { useRef, useState, useEffect } from "react";
import BFLImage from "@repo/shared-ui/BFLImage";
import { redirectWithUtmParam } from "@repo/shared-utils/common-utils";
import { saveLead, sendMobileOtp, validateMobileOtp } from "../../services/AMCService";

// Define the props for this component
interface InvestNowOtpVerifyModalProps {
  onValidationSuccess?: (otpValue: string) => void;
  onValidationFailure?: (otpValue: string) => void;
  mobileNumber?: string;
  onValidate?: (isValid: boolean, otpValue: string) => void;
}

const InvestNowOtpVerifyModal = ({
  onValidationSuccess,
  onValidate,
  mobileNumber = "",
  onValidationFailure,
}: InvestNowOtpVerifyModalProps = {}) => {
  // State for OTP inputs
  const [errors, setErrors] = useState<boolean>(false);
  const [otpValues, setOtpValues] = useState<string[]>(["", "", "", ""]);
  const [resendTimer, setResendTimer] = useState<number>(30);
  const [canResend, setCanResend] = useState<boolean>(false);
  const [isResending, setIsResending] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // References for input fields
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Handle timer for resend OTP
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  // Handle OTP input change
  const handleChange = (index: number, value: string) => {
    // Only allow numeric values
    if (!/^\d*$/.test(value)) return;

    const newOtpValues = [...otpValues];
    // Only take the first digit
    newOtpValues[index] = value.slice(0, 1);
    setOtpValues(newOtpValues);

    // Clear errors when user types
    if (errors) {
      setErrors(false);
    }

    // Auto focus to next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Validate OTP
  const validateOtp = async () => {
    const isValid = otpValues.every((val) => val.trim() !== "");
    const otpCode = otpValues.join("");

    // If onValidate prop is provided, call it with validation result
    if (onValidate) {
      onValidate(isValid, otpCode);
    }

    if (!isValid) {
      setErrors(true);
      return false;
    }

    try {
      setIsLoading(true);
      setErrors(false);

      // Call OTP validation API
      const validationResponse = await validateMobileOtp(mobileNumber, otpCode);

      // Check if OTP validation was successful
      if (validationResponse.statusCode === 90 && validationResponse.status === "success") {
        // Call Save Lead mock before redirect
        await saveLead(mobileNumber);

        // Get the redirect URL from response
        const redirectUrl = validationResponse.data?.Response?.url;

        // Add UTM parameters to the URL
        redirectWithUtmParam(redirectUrl, "true");

        // Call success callback
        onValidationSuccess?.(otpCode);
      } else {
        setErrors(true);
        console.error("OTP validation failed:", validationResponse.message);

        // Call failure callback
        onValidationFailure?.(otpCode);
      }
    } catch (e) {
      setErrors(true);
      console.warn("Error validating OTP:", e);

      // Call failure callback
      onValidationFailure?.(otpCode);
    } finally {
      setIsLoading(false);
    }

    return isValid;
  };

  // Handle resend OTP
  const handleResend = async () => {
    if (canResend && !isResending) {
      try {
        setIsResending(true);

        // Call for send OTP API
        const otpResponse = await sendMobileOtp(mobileNumber);

        // Check if OTP was sent successfully
        if (otpResponse.statusCode === 90 && otpResponse.status === "success") {
          // Reset OTP values and timer on successful resend
          setOtpValues(["", "", "", ""]);
          setCanResend(false);
          setResendTimer(30);
          setErrors(false);
          inputRefs.current?.[0]?.focus();
        } else {
          console.warn("Resend OTP API failed", otpResponse.message);
        }
      } catch (e) {
        console.error("Error in resending OTP:", e);
      } finally {
        setIsResending(false);
      }
    }
  };

  // Handle key navigation (backspace)
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="flex flex-col items-center">
      <BFLImage
        unoptimized={true}
        className="otp-image"
        alt="Otp"
        height={39}
        priority={false}
        src="https://cms-assets.bajajfinserv.in/is/image/bajajfinance/otp-6?scl=1&fmt=png-alpha"
        width={100}
        loading="lazy"
      />
      <div className="otp-section mt-5 flex gap-2">
        {["first", "second", "third", "fourth"].map((position, i) => (
          <input
            type="text"
            ref={(el) => {
              inputRefs.current[i] = el;
            }}
            inputMode="numeric"
            autoFocus={i === 0}
            maxLength={1}
            key={`otp-${position}`}
            aria-label={`OTP Digit ${i + 1}`}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onChange={(e) => handleChange(i, e.target.value)}
            value={otpValues[i]}
            className={`common-input-class h-12 w-12 rounded border ${
              errors && !otpValues[i] ? "border-red-600" : "border-gray-300"
            } text-center focus:ring-2 focus:ring-blue-500 focus:outline-none`}
          />
        ))}
      </div>

      {errors && <div className="mt-2 text-sm text-red-600">{INVEST_NOW_MODAL_KEY.enterOtp}</div>}

      <div className="mt-2 text-xs text-gray-600">
        {canResend ? (
          <button
            disabled={isResending}
            onClick={async () => {
              await handleResend();
            }}
            className={`text-link-primary underline ${isResending ? "cursor-not-allowed opacity-50" : ""}`}
          >
            {isResending ? "SENDING..." : "RESEND OTP"}
          </button>
        ) : (
          <span>Resend OTP in {resendTimer}s</span>
        )}
      </div>

      <span className="gray-600 pt-5 text-xs">
        Powered by Bajaj Financial Securities Limited (Bajaj Broking)
      </span>

      <button
        disabled={isLoading}
        className={`mt-4 cursor-pointer rounded-[200px] bg-gradient-to-r from-[#ff6700] to-[#ff8900] px-[78px] py-[11px] text-sm leading-[18px] font-medium text-white ${isLoading ? "cursor-not-allowed opacity-50" : ""}`}
        onClick={async (e) => {
          e.preventDefault();
          await validateOtp();
        }}
      >
        {isLoading ? "VALIDATING..." : "Submit"}
      </button>
    </div>
  );
};

export default InvestNowOtpVerifyModal;
